import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, X, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import ClientLogo from "./ClientLogo";
import ClientDocumentsPanel from "./ClientDocumentsPanel";
import ClientContactsTab from "./ClientContactsTab";
import ClientNotesTab from "./ClientNotesTab";
import DeleteClientModal from "../../components/DeleteClientModal";
import DeleteUserModal from "../../components/DeleteUserModal";
import { CellulariEditor, CellulariList, SitoWebLink } from "./DatiSocietariShared";

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const date = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${date}, ${time}`;
}

function StatusChip({ status }) {
    if (status === "active") return <span className="lt-chip lt-chip--active"><span className="lt-chip-dot" />Attiva</span>;
    return <span className="lt-chip lt-chip--inactive">Inattiva</span>;
}

function CuStatusChip({ cu }) {
    if (!cu.active) return <span className="lt-chip lt-chip--inactive">Disattivo</span>;
    if (cu.invite_status === "invited") return <span className="lt-chip lt-chip--invited">Invitato</span>;
    return <span className="lt-chip lt-chip--active"><span className="lt-chip-dot" />Attivo</span>;
}

function AddUserModal({ open, onClose, onConfirm, loading }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    useEffect(() => { if (!open) { setName(""); setEmail(""); } }, [open]);
    if (!open) return null;
    return (
        <div className="lt-modal-overlay" role="dialog" data-testid="add-cu-modal">
            <form className="lt-modal" onSubmit={(e) => { e.preventDefault(); onConfirm({ name: name.trim(), email: email.trim() }); }}>
                <div className="flex items-center justify-between" style={{ marginBottom: "18px" }}>
                    <h2 className="lt-section-title" style={{ margin: 0 }}>Aggiungi utente cliente</h2>
                    <button type="button" onClick={onClose} className="lt-btn-ghost" style={{ padding: "6px" }} data-testid="add-cu-close">
                        <X size={16} />
                    </button>
                </div>
                <label className="lt-label">Nome</label>
                <input className="lt-field" value={name} onChange={(e) => setName(e.target.value)} required data-testid="add-cu-name" placeholder="Nome e cognome" />
                <label className="lt-label">Email</label>
                <input className="lt-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="add-cu-email" placeholder="nome@azienda.it" />
                <div className="flex items-center justify-end gap-3" style={{ marginTop: "4px" }}>
                    <button type="button" className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                    <button type="submit" className="lt-btn-primary" disabled={loading} data-testid="add-cu-submit">
                        {loading ? "Invio…" : "Invia invito"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = (searchParams.get("tab") || "panoramica");
    const setActiveTab = (t) => setSearchParams({ tab: t }, { replace: true });
    const { user: authUser } = useAuth();
    const isSuperAdmin = !!authUser?.is_super_admin;
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [internalUsers, setInternalUsers] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [deleteClientOpen, setDeleteClientOpen] = useState(false);
    const [deleteCu, setDeleteCu] = useState(null); // client_user object or null
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/internal/clients/${id}`);
            setClient(data);
            setForm({
                company_name: data.company_name || "",
                vat_or_taxcode: data.vat_or_taxcode || "",
                address: data.address || "",
                pec_email: data.pec_email || "",
                assigned_internal_user_id: data.assigned_internal_user?.id || "",
                // Phase 9 Dati societari
                capitale_sociale: data.capitale_sociale || "",
                numero_rea: data.numero_rea || "",
                amministratore: data.amministratore || "",
                sede_legale: data.sede_legale || "",
                sede_operativa: data.sede_operativa || "",
                cellulari: Array.isArray(data.cellulari) ? data.cellulari : [],
                sito_web: data.sito_web || "",
                sdi_code: data.sdi_code || "",
                iban: data.iban || "",
            });
        } catch {
            toast.error("Società non trovata");
            navigate("/app/clienti");
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (editing && internalUsers.length === 0) {
            api.get("/internal/internal-users", { params: { active: true } })
                .then(({ data }) => setInternalUsers(data.items || []))
                .catch(() => {});
        }
    }, [editing, internalUsers.length]);

    const saveEdit = async () => {
        try {
            await api.patch(`/internal/clients/${id}`, form);
            toast.success("Modifiche salvate");
            setEditing(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
        }
    };

    const toggleStatus = async () => {
        const action = client.status === "active" ? "deactivate" : "reactivate";
        try {
            await api.post(`/internal/clients/${id}/${action}`);
            toast.success(action === "deactivate" ? "Società disattivata" : "Società riattivata");
            load();
        } catch {
            toast.error("Operazione fallita");
        }
    };

    const onLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/internal/clients/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Logo aggiornato");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload fallito");
        } finally {
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const addClientUser = async ({ name, email }) => {
        setAddLoading(true);
        try {
            await api.post(`/internal/clients/${id}/client-users`, { name, email });
            toast.success(`Invito inviato a ${email}`);
            setAddOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setAddLoading(false);
        }
    };

    const resendInvite = async (cuId, email) => {
        try {
            await api.post(`/internal/clients/${id}/client-users/${cuId}/resend-invite`);
            toast.success(`Invito reinviato a ${email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operazione fallita");
        }
    };

    const toggleCuStatus = async (cu) => {
        const action = cu.active ? "deactivate" : "reactivate";
        try {
            await api.post(`/internal/clients/${id}/client-users/${cu.id}/${action}`);
            toast.success(action === "deactivate" ? "Utente disattivato" : "Utente riattivato");
            load();
        } catch {
            toast.error("Operazione fallita");
        }
    };

    if (loading || !client) {
        return <div style={{ padding: "32px 40px", color: "#8A8F88" }} data-testid="client-detail-loading">Caricamento…</div>;
    }

    return (
        <div style={{ padding: "32px 40px" }} data-testid="client-detail-page">
            <Link to="/app/clienti" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "20px" }} data-testid="client-detail-back-link">
                <ArrowLeft size={14} /> Torna alle società clienti
            </Link>

            <div className="flex items-center gap-5" style={{ marginBottom: "24px", flexWrap: "wrap" }}>
                <ClientLogo client={client} size={64} />
                <div className="flex-1 min-w-0">
                    <h1 className="lt-h1" data-testid="client-detail-heading">{client.company_name}</h1>
                    <div className="flex items-center gap-3" style={{ marginTop: "8px", flexWrap: "wrap" }}>
                        <StatusChip status={client.status} />
                        <span style={{ color: "#8A8F88", fontSize: "13px", fontFamily: "'IBM Plex Mono', monospace" }}>{client.vat_or_taxcode}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {!editing && (
                        <button type="button" className="lt-btn-secondary" onClick={() => setEditing(true)} data-testid="client-edit-btn">Modifica</button>
                    )}
                    {client.status === "active" ? (
                        <button type="button" className="lt-btn-danger-text" onClick={toggleStatus} data-testid="client-toggle-status-btn">Disattiva</button>
                    ) : (
                        <button type="button" className="lt-btn-secondary" onClick={toggleStatus} data-testid="client-toggle-status-btn">Riattiva</button>
                    )}
                    {isSuperAdmin && (
                        <button
                            type="button"
                            onClick={() => setDeleteClientOpen(true)}
                            data-testid="client-delete-btn"
                            title="Solo super admin — eliminazione definitiva del cliente e di tutti i dati collegati"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "8px 14px",
                                borderRadius: 2,
                                border: "1px solid rgba(220, 38, 38, 0.4)",
                                background: "transparent",
                                color: "#DC2626",
                                fontFamily: "'Archivo', system-ui, sans-serif",
                                fontWeight: 500,
                                fontSize: 14,
                                cursor: "pointer",
                                transition: "background-color .15s ease, border-color .15s ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220, 38, 38, 0.08)"; e.currentTarget.style.borderColor = "#DC2626"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.4)"; }}
                        >
                            <Trash2 size={14} /> Elimina definitivamente
                        </button>
                    )}
                </div>
            </div>

            {/* TAB BAR */}
            <div style={{
                display: "flex",
                gap: 0,
                borderBottom: "1px solid #E3E5E1",
                marginBottom: 24,
                overflowX: "auto",
            }} data-testid="client-tabs">
                {[
                    { key: "panoramica", label: "Panoramica" },
                    { key: "contatti", label: "Contatti" },
                    { key: "note", label: "Note" },
                    { key: "documenti", label: "Documenti" },
                ].map((t) => {
                    const active = activeTab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setActiveTab(t.key)}
                            data-testid={`client-tab-${t.key}`}
                            style={{
                                padding: "14px 22px",
                                background: "transparent",
                                border: "none",
                                borderBottom: `2px solid ${active ? "#E5FF00" : "transparent"}`,
                                marginBottom: -1,
                                color: active ? "#181B20" : "#5F6560",
                                fontWeight: active ? 600 : 500,
                                fontFamily: '"IBM Plex Mono", monospace',
                                fontSize: 12,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                cursor: "pointer",
                                transition: "color .12s ease, border-color .12s ease",
                                whiteSpace: "nowrap",
                            }}
                        >{t.label}</button>
                    );
                })}
            </div>

            {activeTab === "contatti" && <ClientContactsTab clientId={id} />}
            {activeTab === "note" && <ClientNotesTab clientId={id} />}
            {activeTab === "documenti" && <ClientDocumentsPanel clientId={id} clientName={client.company_name} />}
            {activeTab === "panoramica" && (
            <>
            <div className="grid gap-6" style={{ gridTemplateColumns: "1.4fr 1fr" }}>
                <div className="lt-card" style={{ padding: "28px" }} data-testid="client-info-panel">
                    <h2 className="lt-section-title">Dati società</h2>

                    {editing ? (
                        <>
                            <label className="lt-label">Ragione sociale</label>
                            <input className="lt-field" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="client-edit-company-name" />
                            <label className="lt-label">P.IVA o CF</label>
                            <input className="lt-field" value={form.vat_or_taxcode} onChange={(e) => setForm({ ...form, vat_or_taxcode: e.target.value })} data-testid="client-edit-vat" />
                            <label className="lt-label">Indirizzo</label>
                            <input className="lt-field" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-edit-address" />
                            <label className="lt-label">PEC</label>
                            <input className="lt-field" type="email" value={form.pec_email} onChange={(e) => setForm({ ...form, pec_email: e.target.value })} data-testid="client-edit-pec" />
                            <label className="lt-label">Referente interno</label>
                            <select className="lt-field" value={form.assigned_internal_user_id} onChange={(e) => setForm({ ...form, assigned_internal_user_id: e.target.value })} data-testid="client-edit-assigned">
                                <option value="" disabled>Seleziona…</option>
                                {internalUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>

                            <div className="lt-divider" />

                            <h3 className="lt-section-title" data-testid="client-edit-dati-soc-title" style={{ marginTop: 4 }}>Dati societari</h3>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                                <div>
                                    <label className="lt-label">Capitale sociale</label>
                                    <input className="lt-field" value={form.capitale_sociale || ""} onChange={(e) => setForm({ ...form, capitale_sociale: e.target.value })} data-testid="client-edit-capitale-sociale" placeholder="€ 10.000,00 i.v." />
                                </div>
                                <div>
                                    <label className="lt-label">Numero REA</label>
                                    <input className="lt-field" value={form.numero_rea || ""} onChange={(e) => setForm({ ...form, numero_rea: e.target.value })} data-testid="client-edit-numero-rea" placeholder="MI-1234567" />
                                </div>
                            </div>
                            <label className="lt-label">Amministratore</label>
                            <input className="lt-field" value={form.amministratore || ""} onChange={(e) => setForm({ ...form, amministratore: e.target.value })} data-testid="client-edit-amministratore" />
                            <label className="lt-label">Sede legale</label>
                            <input className="lt-field" value={form.sede_legale || ""} onChange={(e) => setForm({ ...form, sede_legale: e.target.value })} data-testid="client-edit-sede-legale" />
                            <label className="lt-label">Sede operativa</label>
                            <input className="lt-field" value={form.sede_operativa || ""} onChange={(e) => setForm({ ...form, sede_operativa: e.target.value })} data-testid="client-edit-sede-operativa" />
                            <label className="lt-label">Cellulari</label>
                            <CellulariEditor
                                values={form.cellulari && form.cellulari.length ? form.cellulari : [""]}
                                onChange={(next) => setForm({ ...form, cellulari: next })}
                                testid="client-edit-cellulari-editor"
                            />
                            <label className="lt-label" style={{ marginTop: 16 }}>Sito web</label>
                            <input className="lt-field" value={form.sito_web || ""} onChange={(e) => setForm({ ...form, sito_web: e.target.value })} data-testid="client-edit-sito-web" placeholder="www.acme.it" />

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", marginTop: 8 }}>
                                <div>
                                    <label className="lt-label">Codice SDI</label>
                                    <input className="lt-field" value={form.sdi_code || ""} onChange={(e) => setForm({ ...form, sdi_code: e.target.value.toUpperCase() })} maxLength={7} minLength={0} placeholder="7 caratteri" data-testid="client-edit-sdi-code" />
                                </div>
                                <div>
                                    <label className="lt-label">IBAN</label>
                                    <input className="lt-field" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase().replace(/\s/g, "") })} maxLength={34} placeholder="IT60X0542811101000000123456" data-testid="client-edit-iban" />
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3" style={{ marginTop: "4px" }}>
                                <button type="button" className="lt-btn-secondary" onClick={() => { setEditing(false); load(); }}>Annulla</button>
                                <button type="button" className="lt-btn-primary" onClick={saveEdit} data-testid="client-edit-save">Salva</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <dl style={{ margin: 0 }}>
                                {[
                                    ["Ragione sociale", client.company_name],
                                    ["P.IVA o CF", client.vat_or_taxcode],
                                    ["Indirizzo", client.address || "—"],
                                    ["PEC", client.pec_email],
                                    ["Referente interno", client.assigned_internal_user?.name || "—"],
                                ].map(([k, v]) => (
                                    <div key={k} style={{ display: "grid", gridTemplateColumns: "200px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                                        <dt className="lt-label" style={{ margin: 0 }}>{k}</dt>
                                        <dd style={{ margin: 0, color: "#181B20", fontSize: "14px" }}>{v}</dd>
                                    </div>
                                ))}
                            </dl>

                            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #E3E5E1" }} data-testid="client-view-dati-soc">
                                <h3 className="lt-section-title">Dati societari</h3>
                                <dl style={{ margin: 0 }}>
                                    {[
                                        ["Capitale sociale", client.capitale_sociale || "—", "capitale-sociale"],
                                        ["Numero REA", client.numero_rea || "—", "numero-rea"],
                                        ["Amministratore", client.amministratore || "—", "amministratore"],
                                        ["Sede legale", client.sede_legale || "—", "sede-legale"],
                                        ["Sede operativa", client.sede_operativa || "—", "sede-operativa"],
                                        ["Codice SDI", client.sdi_code || "—", "sdi-code"],
                                        ["IBAN", client.iban || "—", "iban"],
                                    ].map(([k, v, tid]) => (
                                        <div key={k} style={{ display: "grid", gridTemplateColumns: "200px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                                            <dt className="lt-label" style={{ margin: 0 }}>{k}</dt>
                                            <dd style={{ margin: 0, color: "#181B20", fontSize: "14px" }} data-testid={`client-view-${tid}`}>{v}</dd>
                                        </div>
                                    ))}
                                    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                                        <dt className="lt-label" style={{ margin: 0 }}>Cellulari</dt>
                                        <dd style={{ margin: 0, color: "#181B20", fontSize: "14px" }} data-testid="client-view-cellulari">
                                            <CellulariList items={client.cellulari} testid="client-view-cellulari-list" />
                                        </dd>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", padding: "10px 0" }}>
                                        <dt className="lt-label" style={{ margin: 0 }}>Sito web</dt>
                                        <dd style={{ margin: 0, color: "#181B20", fontSize: "14px" }} data-testid="client-view-sito-web">
                                            <SitoWebLink value={client.sito_web} testid="client-view-sito-web-link" />
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </>
                    )}

                    <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #E3E5E1" }}>
                        <h2 className="lt-section-title">Logo</h2>
                        <input ref={fileRef} type="file" accept="image/*" onChange={onLogoChange} style={{ display: "none" }} data-testid="client-logo-input" />
                        <button type="button" className="lt-btn-secondary" onClick={() => fileRef.current?.click()} data-testid="client-logo-upload-btn">
                            <Upload size={14} /> {client.has_logo ? "Sostituisci logo" : "Carica logo"}
                        </button>
                        <p className="lt-helper" style={{ marginTop: "8px" }}>PNG, JPG, WEBP o SVG · max 2 MB</p>
                    </div>
                </div>

                <div className="lt-card" style={{ padding: "28px" }} data-testid="client-users-panel">
                    <div className="flex items-center justify-between" style={{ marginBottom: "12px" }}>
                        <h2 className="lt-section-title" style={{ margin: 0 }}>Utenti cliente</h2>
                        <button type="button" className="lt-btn-secondary" onClick={() => setAddOpen(true)} data-testid="client-add-user-btn">Aggiungi utente</button>
                    </div>

                    <div className="flex flex-col">
                        {client.client_users?.length === 0 && (
                            <p className="lt-helper" style={{ margin: "8px 0 0 0" }}>Nessun utente.</p>
                        )}
                        {client.client_users?.map((cu) => (
                            <div key={cu.id} data-testid={`cu-row-${cu.id}`}
                                style={{ padding: "14px 0", borderBottom: "1px solid #EEEFEA" }}>
                                <div className="flex items-center justify-between" style={{ gap: "12px" }}>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ color: "#181B20", fontSize: "14px", fontWeight: 500 }}>{cu.name}</div>
                                        <div style={{ color: "#5F6560", fontSize: "13px", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis" }}>{cu.email}</div>
                                        <div style={{ color: "#8A8F89", fontSize: "12px", marginTop: "4px", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.04em" }} data-testid={`cu-last-login-${cu.id}`}>
                                            Ultimo accesso: <span style={{ textTransform: "none", letterSpacing: 0 }}>{fmtDateTime(cu.last_login_at)}</span>
                                        </div>
                                    </div>
                                    <CuStatusChip cu={cu} />
                                </div>
                                <div className="flex items-center gap-2" style={{ marginTop: "6px", marginLeft: "-8px" }}>
                                    {cu.invite_status === "invited" && cu.active && (
                                        <button type="button" className="lt-btn-ghost" onClick={() => resendInvite(cu.id, cu.email)} data-testid={`cu-resend-${cu.id}`}>
                                            Reinvia invito
                                        </button>
                                    )}
                                    {cu.active ? (
                                        <button type="button" className="lt-btn-danger-text" onClick={() => toggleCuStatus(cu)} data-testid={`cu-toggle-${cu.id}`}>
                                            Disattiva
                                        </button>
                                    ) : (
                                        <button type="button" className="lt-btn-ghost" onClick={() => toggleCuStatus(cu)} data-testid={`cu-toggle-${cu.id}`}>
                                            Riattiva
                                        </button>
                                    )}
                                    {isSuperAdmin && (
                                        <button
                                            type="button"
                                            onClick={() => setDeleteCu(cu)}
                                            data-testid={`cu-delete-${cu.id}`}
                                            title="Solo super admin — eliminazione definitiva utente"
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 4,
                                                padding: "6px 10px",
                                                borderRadius: 2,
                                                border: "1px solid transparent",
                                                background: "transparent",
                                                color: "#DC2626",
                                                fontFamily: "'Archivo', system-ui, sans-serif",
                                                fontSize: 13,
                                                cursor: "pointer",
                                                transition: "background-color .15s ease, border-color .15s ease",
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220, 38, 38, 0.08)"; e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.3)"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
                                        >
                                            <Trash2 size={12} /> Elimina
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            </>
            )}

            <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} onConfirm={addClientUser} loading={addLoading} />

            <DeleteClientModal
                open={deleteClientOpen}
                onClose={() => setDeleteClientOpen(false)}
                client={client}
                onDeleted={() => {
                    setDeleteClientOpen(false);
                    toast.success(`Cliente "${client.company_name}" eliminato definitivamente`);
                    navigate("/app/clienti");
                }}
            />

            <DeleteUserModal
                open={!!deleteCu}
                onClose={() => setDeleteCu(null)}
                user={deleteCu || undefined}
                endpoint={deleteCu ? `/internal/clients/${id}/client-users/${deleteCu.id}` : ""}
                title="Eliminazione definitiva utente cliente"
                onDeleted={() => {
                    const name = deleteCu?.name;
                    setDeleteCu(null);
                    toast.success(`Utente ${name} eliminato definitivamente`);
                    load();
                }}
            />
        </div>
    );
}
